'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { fetchCustomRules } from './api';
import { BASE_MAX_ENABLED_RULES } from '@/lib/custom-rule-limits';

/**
 * Query key for fetching all custom rules for an installation.
 * This is separate from the repository-specific query key used in use-custom-rules.ts
 */
export const installationCustomRulesQueryKey = (installationId: number | null) => {
  return ['installation-custom-rules-v2', installationId];
};

/**
 * Hook for fetching all custom rules for an installation.
 * This is specifically designed for the rules library page which needs to view
 * all rules across all repositories, without repository-specific mutations.
 *
 * For repository-specific operations with mutations (create, update, delete),
 * use useCustomRules from use-custom-rules.ts instead.
 *
 * @param installationId - The installation ID to fetch rules for
 * @returns Query result with all custom rules for the installation
 */
export function useInstallationCustomRules(installationId) {
  const $ = _c(17);
  let t0;
  if ($[0] !== installationId) {
    t0 = installationCustomRulesQueryKey(installationId);
    $[0] = installationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== installationId) {
    t1 = () => {
      if (!installationId) {
        return Promise.resolve({
          rules: [],
          maxEnabledRules: BASE_MAX_ENABLED_RULES,
          notRunningRuleIds: []
        });
      }
      return fetchCustomRules(installationId, null);
    };
    $[2] = installationId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(installationId);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 300000,
      gcTime: 600000
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data,
    isLoading,
    isFetching,
    isError,
    isSuccess
  } = useQuery(t3);
  let t4;
  if ($[8] !== data?.rules) {
    t4 = data?.rules ?? [];
    $[8] = data?.rules;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = data?.maxEnabledRules ?? BASE_MAX_ENABLED_RULES;
  let t6;
  if ($[10] !== isError || $[11] !== isFetching || $[12] !== isLoading || $[13] !== isSuccess || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      rules: t4,
      maxEnabledRules: t5,
      isLoading,
      isFetching,
      isError,
      isSuccess
    };
    $[10] = isError;
    $[11] = isFetching;
    $[12] = isLoading;
    $[13] = isSuccess;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}